import { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { ArrowLeft, CheckCircle2, Circle, Users, Target } from 'lucide-react';
import { api } from '../lib/api';
import { useI18n } from '../lib/i18n';

const STATUS_STYLE = {
  idea: { bg: 'rgba(100,116,139,0.10)', color: '#64748B' },
  planning: { bg: 'rgba(14,165,233,0.10)', color: '#0EA5E9' },
  active: { bg: 'rgba(16,185,129,0.10)', color: '#10B981' },
  completed: { bg: 'rgba(139,92,246,0.10)', color: '#8B5CF6' },
  paused: { bg: 'rgba(245,158,11,0.12)', color: '#F59E0B' },
};

export default function ProjectDetail() {
  const { id } = useParams();
  const { t, lang } = useI18n();
  const [p, setP] = useState(null);
  const [loading, setLoading] = useState(true);
  useEffect(() => { (async () => {
    try { const { data } = await api.get(`/projects/${id}`); setP(data); }
    finally { setLoading(false); }
  })(); }, [id]);

  if (loading) return <div className="max-w-3xl mx-auto px-6 py-24 text-muted-foreground">{t.common.loading}</div>;
  if (!p) return <div className="max-w-3xl mx-auto px-6 py-24">Not found.</div>;

  const st = STATUS_STYLE[p.status] || STATUS_STYLE.planning;

  return (
    <div className="max-w-4xl mx-auto px-6 lg:px-10 py-16 lg:py-24">
      <Link to="/projects" className="inline-flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground mb-10" data-testid="back-to-projects">
        <ArrowLeft size={16} /> {t.projects.title}
      </Link>
      <div className="flex flex-wrap items-center gap-3 mb-6">
        <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium" style={{ color: st.color, backgroundColor: st.bg }}>
          {t.projects.status[p.status] || p.status}
        </span>
        <span className="text-xs uppercase tracking-widest text-muted-foreground">{p.category}</span>
      </div>
      <h1 className="font-display text-4xl md:text-5xl font-medium tracking-tight leading-tight text-balance">
        {lang === 'it' ? p.title_it : p.title_en}
      </h1>
      <p className="mt-5 text-xl text-muted-foreground leading-relaxed">{lang === 'it' ? p.summary_it : p.summary_en}</p>

      <div className="mt-10 grid grid-cols-2 gap-4">
        <div className="p-5 rounded-2xl border border-border bg-card">
          <div className="text-xs uppercase tracking-widest text-muted-foreground mb-2 inline-flex items-center gap-1.5"><Target size={12} /> {t.projects.budget}</div>
          <div className="font-display text-2xl font-medium">€{p.budget_eur?.toLocaleString?.() || 0}</div>
        </div>
        <div className="p-5 rounded-2xl border border-border bg-card">
          <div className="text-xs uppercase tracking-widest text-muted-foreground mb-2 inline-flex items-center gap-1.5"><Users size={12} /> {t.projects.volunteers_needed}</div>
          <div className="font-display text-2xl font-medium">{p.volunteers_needed}</div>
        </div>
      </div>

      <h2 className="font-display text-2xl md:text-3xl font-medium tracking-tight mt-14 mb-6">{t.projects.roadmap}</h2>
      <ol className="space-y-3">
        {(p.roadmap || []).map((r, i) => (
          <li key={i} data-testid={`roadmap-item-${i}`} className={`p-5 rounded-2xl border ${r.done ? 'border-emerald-500/30 bg-emerald-500/5' : 'border-border bg-card'} flex items-start gap-4`}>
            {r.done ? <CheckCircle2 size={20} className="text-emerald-500 mt-0.5 flex-shrink-0" /> : <Circle size={20} className="text-muted-foreground mt-0.5 flex-shrink-0" />}
            <div className="flex-1">
              <div className={`font-medium ${r.done ? 'line-through text-muted-foreground' : ''}`}>{r.title}</div>
              {r.date && <div className="text-xs text-muted-foreground mt-1">{r.date}</div>}
            </div>
          </li>
        ))}
      </ol>
    </div>
  );
}
